import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  appExpiryQuery,
  expiringAppsQuery,
  saveAppExpiry,
  type AppExpiryPage,
  type ExpiringPage,
  type ExpiryItem,
  type ExpiryLevel,
} from '../../api/appExpiry'
import type { BusinessAppSummary, MissingItem } from '../../api/businessApps'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { appUrl, failed } from '../parts'
import './AppExpiry.css'

/**
 * EXPIRARILE unei aplicatii de business (`v4/api/src/BusinessApps/Expiry/`):
 * contractul de suport, licenta, data de reinnoire si certificatele TLS de pe
 * componente (inventarul agentului, `it_certificates`).
 *
 *  - `ExpiryBanner`   sus pe fisa: "Support contract expires in 23 days" - tot
 *                     ce a intrat in primele 60 de zile sau a expirat;
 *  - `ExpiryCard`     datele, cu "Edit dates" pentru cine poate scrie, si lista
 *                     tuturor expirarilor aplicatiei;
 *  - `ExpiringPanel`  fila "Expiring" din Overview > Applications: tot ce
 *                     expira in urmatoarele 90 de zile;
 *  - `withExpiry`     pune expirarile apropiate in banda "applications need
 *                     completing".
 *
 * Alerta din clopotel (60 / 30 / 7 zile si la expirare) o trimite cronul zilnic.
 * Fara drept sau inainte de migrare, nimic din ce e aici nu apare.
 */

const PILL: Record<ExpiryLevel, string> = {
  expired: 'adm-pill-bad',
  soon: 'adm-pill-bad',
  near: 'adm-pill-warn',
  ahead: 'adm-pill-warn',
  later: 'adm-pill-ok',
}

function daysLabel(days: number): string {
  if (days === 0) return 'today'
  if (days < 0) return `expired ${-days} day${days === -1 ? '' : 's'} ago`
  return `in ${days} day${days === 1 ? '' : 's'}`
}

export function ExpiryPill({ item }: { item: Pick<ExpiryItem, 'days' | 'level'> }) {
  return <span className={`adm-pill ${PILL[item.level]} app-expiry-pill`}>{daysLabel(item.days)}</span>
}

export function ExpiryBanner({ appId }: { appId: number }) {
  const page = useQuery({ ...appExpiryQuery(appId), retry: false })
  const banner = page.data?.ready ? (page.data.banner ?? []) : []
  if (banner.length === 0) return null
  const tone = banner.some((b) => b.days <= 7) ? 'alert-danger' : 'alert-warning'

  return (
    <div className={`alert ${tone} app-expiry-banner`} role="alert" id="appExpiryBanner">
      <i className="fas fa-hourglass-half" aria-hidden="true"></i>
      <ul>
        {banner.map((b) => (
          <li key={`${b.kind}-${b.ref}`}>
            <strong>{b.text}</strong> <span className="app-expiry-date">({b.date})</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function ExpiryCard({ appId }: { appId: number }) {
  const page = useQuery({ ...appExpiryQuery(appId), retry: false })
  const [editing, setEditing] = useState(false)
  if (!page.data?.ready) return null
  const data = page.data
  const items = data.items ?? []
  const dates = data.dates

  return (
    <>
      <div className="adm-section">
        <h4>Expiry</h4>
        <span className="adm-section-note">support contract, license, renewal and the TLS certificates of the servers</span>
        {data.canEdit && (
          <button type="button" className="btn btn-default btn-xs" id="appExpiryEdit" style={{ marginLeft: 'auto' }} onClick={() => setEditing(true)}>
            <i className="fa fa-pencil-alt"></i> Edit dates
          </button>
        )}
      </div>
      <div className="adm-card" id="appExpiry">
        <dl className="app-expiry-dates">
          <dt>Support contract ends</dt>
          <dd>{dates?.supportContractEnd ?? <span className="adm-muted">not filled in</span>}</dd>
          <dt>License expires</dt>
          <dd>
            {dates?.licenseExpires ?? <span className="adm-muted">not filled in</span>}
            {dates?.licenseNote ? <span className="adm-muted"> &middot; {dates.licenseNote}</span> : null}
          </dd>
          <dt>Renewal date</dt>
          <dd>{dates?.renewalDate ?? <span className="adm-muted">not filled in</span>}</dd>
        </dl>
        {items.length === 0 ? (
          <p className="text-muted app-expiry-empty">Nothing to watch yet: no dates, and no TLS certificate found on the servers.</p>
        ) : (
          <table className="table adm-list" id="appExpiryItems">
            <thead>
              <tr>
                <th>What</th>
                <th>Date</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={`${i.kind}-${i.ref}`} className={i.days <= 7 ? 'app-expiry-urgent' : undefined}>
                  <td>
                    {i.label}
                    {i.issuer ? <div className="adm-muted small">{i.issuer}</div> : null}
                  </td>
                  <td>{i.date}</td>
                  <td>
                    <ExpiryPill item={i} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {editing && <ExpiryModal appId={appId} page={data} onClose={() => setEditing(false)} />}
    </>
  )
}

function ExpiryModal({ appId, page, onClose }: { appId: number; page: AppExpiryPage; onClose(): void }) {
  const query = useQueryClient()
  const [support, setSupport] = useState(page.dates?.supportContractEnd ?? '')
  const [license, setLicense] = useState(page.dates?.licenseExpires ?? '')
  const [renewal, setRenewal] = useState(page.dates?.renewalDate ?? '')
  const [note, setNote] = useState(page.dates?.licenseNote ?? '')
  const [busy, setBusy] = useState(false)

  const save = () => {
    setBusy(true)
    saveAppExpiry(appId, { support_contract_end: support, license_expires: license, renewal_date: renewal, license_note: note })
      .then((r) => {
        toast(r.message, 'Applications', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') {
          void query.invalidateQueries({ queryKey: ['business-apps'] })
          onClose()
        }
      })
      .catch((e) => toast(failed(e), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id={`appExpiry-${appId}`}
      open
      onClose={onClose}
      title="Expiry dates"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" id="appExpirySave" disabled={busy} onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="expSupport">Support contract ends</label>
        <input id="expSupport" type="date" className="form-control" value={support} onChange={(ev) => setSupport(ev.target.value)} />
      </div>
      <div className="form-group">
        <label htmlFor="expLicense">License expires</label>
        <input id="expLicense" type="date" className="form-control" value={license} onChange={(ev) => setLicense(ev.target.value)} />
      </div>
      <div className="form-group">
        <label htmlFor="expNote">License</label>
        <input id="expNote" className="form-control" placeholder="e.g. license number or type" maxLength={190} value={note} onChange={(ev) => setNote(ev.target.value)} />
      </div>
      <div className="form-group">
        <label htmlFor="expRenewal">Renewal date</label>
        <input id="expRenewal" type="date" className="form-control" value={renewal} onChange={(ev) => setRenewal(ev.target.value)} />
        <p className="help-block">
          The technical owner of the application and the customer&apos;s account manager get a notification at 60, 30 and 7 days, and when it
          expires. TLS certificates are read from the servers, no need to enter them.
        </p>
      </div>
    </Modal>
  )
}

export function ExpiringPanel() {
  const page = useQuery({ ...expiringAppsQuery(90), retry: false })
  if (page.error) {
    return (
      <div className="alert alert-danger" style={{ margin: '0 20px 20px' }}>
        Something went wrong: {page.error.message}
      </div>
    )
  }
  if (!page.data) return <p className="text-muted" style={{ margin: '0 20px' }}>Loading...</p>
  const data = page.data
  if (!data.ready) {
    return (
      <div className="alert alert-warning" style={{ margin: '0 20px 20px' }}>
        <i className="fas fa-database"></i> {data.message ?? 'Expiry dates are not set up on this server yet.'}
      </div>
    )
  }

  return (
    <div className="adm-card" style={{ margin: '0 20px 20px' }} id="appsExpiring">
      {data.items.length === 0 ? (
        <p className="text-muted" style={{ padding: '10px 15px' }}>
          Nothing expires in the next {data.days} days: no support contract, license, renewal or TLS certificate.
        </p>
      ) : (
        <table className="table adm-list">
          <thead>
            <tr>
              <th>Application</th>
              <th>What</th>
              <th>Date</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((i) => (
              <tr key={`${i.appId}-${i.kind}-${i.ref}`} className={i.days <= 7 ? 'app-expiry-urgent' : undefined}>
                <td>
                  <a href={appUrl(i.appId)} target="_blank" rel="noopener noreferrer">
                    <strong>{i.appName}</strong>
                  </a>
                </td>
                <td>{i.label}</td>
                <td>{i.date}</td>
                <td>
                  <ExpiryPill item={i} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** Cate expira in primele 60 de zile (sau au expirat) - cifra de pe fila "Expiring". */
export function urgentCount(page: ExpiringPage | undefined): number {
  if (!page?.ready || !Array.isArray(page.items)) return 0
  const limit = page.alertDays ?? 60
  return page.items.filter((i) => i.days <= limit).length
}

/**
 * Aplicatiile cu expirarile apropiate (primele 60 de zile, sau expirate) puse
 * in "Missing" - asa intra in banda "applications need completing" si in
 * semnul rosu de langa nume, fara alta banda.
 */
export function withExpiry(apps: BusinessAppSummary[], page: ExpiringPage | undefined): BusinessAppSummary[] {
  if (!page?.ready || !Array.isArray(page.items) || page.items.length === 0) return apps
  const limit = page.alertDays ?? 60
  const byApp = new Map<number, MissingItem[]>()
  for (const i of page.items) {
    if (i.days > limit) continue
    const list = byApp.get(i.appId) ?? []
    list.push({ key: `expiry:${i.kind}:${i.ref}`, label: i.text, section: 'completeness' })
    byApp.set(i.appId, list)
  }
  if (byApp.size === 0) return apps
  return apps.map((a) => {
    const extra = byApp.get(a.id)
    if (!extra) return a
    const missing = [...(a.missing ?? []), ...extra]
    return { ...a, missing, missingCount: missing.length, needsAttention: true }
  })
}
